import recordConstants from '../../../constants/record';
import { Record as RecordHelper } from '../../../modules/bin/utility';
import {
  AccountClusterCount,
  BodyHashClusterItem,
  DecisionRecord,
  ErrorEntry,
  EventDetailResponse,
  FanOutResult,
  QueueFilters,
  QueueListItem,
} from '../models';
import { aiLeanFromScore } from '../helpers/isDisagreementReasonRequired';
import {
  CONTENT_MODERATION_SLICE_KEY,
  ContentModerationState,
  ContentModerationModal,
} from './contentModerationTypes';

interface RootStateShape {
  [CONTENT_MODERATION_SLICE_KEY]: ContentModerationState;
}

function slice(state: RootStateShape): ContentModerationState {
  return state[CONTENT_MODERATION_SLICE_KEY];
}

// Queue
export const selectQueueItems = (state: RootStateShape): QueueListItem[] =>
  slice(state).queue.items;
export const selectQueueIsLoading = (state: RootStateShape): boolean =>
  slice(state).queue.isLoading;
export const selectQueueIsPolling = (state: RootStateShape): boolean =>
  slice(state).queue.isPolling;
export const selectQueueError = (state: RootStateShape): ErrorEntry | null =>
  slice(state).queue.error;
export const selectQueueFilters = (state: RootStateShape): QueueFilters =>
  slice(state).queue.filters;
export const selectQueueLastFetchedAt = (state: RootStateShape): number | null =>
  slice(state).queue.lastFetchedAt;
export const selectQueueTotalCount = (state: RootStateShape): number =>
  slice(state).queue.totalCount;
export const selectQueuePage = (state: RootStateShape): number =>
  slice(state).queue.page;
export const selectQueuePageSize = (state: RootStateShape): number =>
  slice(state).queue.pageSize;
export const selectQueueIsAppending = (state: RootStateShape): boolean =>
  slice(state).queue.isAppending;
// True when the server thinks there are more rows than we have rendered. Used
// to show / hide the Load-more affordance.
export const selectQueueHasMore = (state: RootStateShape): boolean => {
  const q = slice(state).queue;
  return q.items.length < q.totalCount;
};

// Detail
export const selectDetailReady = (state: RootStateShape): boolean =>
  RecordHelper.isRecordReady(slice(state).detail);
export const selectDetailLoading = (state: RootStateShape): boolean =>
  RecordHelper.isRecordLoading(slice(state).detail);
export const selectDetailData = (state: RootStateShape): EventDetailResponse | null => {
  const detail = slice(state).detail;
  return detail.ready === recordConstants.RECORD_STATUS_READY
    ? (detail.data as EventDetailResponse)
    : null;
};
export const selectDetailError = (state: RootStateShape): string[] | null => {
  const detail = slice(state).detail;
  return detail.error?.show ? (detail.error.errorKeys as string[] | null) : null;
};

// Cluster
export const selectClusterData = (state: RootStateShape): AccountClusterCount | null => {
  const cluster = slice(state).cluster;
  return cluster.ready === recordConstants.RECORD_STATUS_READY
    ? (cluster.data as AccountClusterCount)
    : null;
};

// Body-hash cluster
export const selectBodyHashItems = (state: RootStateShape): BodyHashClusterItem[] => {
  const cluster = slice(state).bodyHashCluster;
  return cluster.ready === recordConstants.RECORD_STATUS_READY
    ? (cluster.data as BodyHashClusterItem[])
    : [];
};
export const selectBodyHashLoading = (state: RootStateShape): boolean =>
  RecordHelper.isRecordLoading(slice(state).bodyHashCluster);

// Lock
export const selectLockState = (state: RootStateShape) => slice(state).lock.state;
export const selectLockHolder = (state: RootStateShape) => slice(state).lock.holder;
export const selectLockLastHeartbeatAt = (state: RootStateShape) =>
  slice(state).lock.lastHeartbeatAt;
export const selectIsLockMine = (
  state: RootStateShape,
  reviewerAccountId: number | null | undefined,
): boolean => {
  const lock = slice(state).lock;
  if (lock.state !== 'Held') return false;
  if (!lock.holder || reviewerAccountId == null) return false;
  return lock.holder.reviewerAccountId === reviewerAccountId;
};

// Decision
export const selectDecisionSubmitLoading = (state: RootStateShape): boolean =>
  RecordHelper.isRecordLoading(slice(state).decisionSubmit);
export const selectDecisionSubmitError = (state: RootStateShape) =>
  slice(state).decisionSubmit.error;
export const selectDecisionResetLoading = (state: RootStateShape): boolean =>
  RecordHelper.isRecordLoading(slice(state).decisionReset);
export const selectLastDecisionRecord = (state: RootStateShape): DecisionRecord | null => {
  const decision = slice(state).decisionSubmit;
  if (decision.ready !== recordConstants.RECORD_STATUS_READY) return null;
  return (decision.data?.decision as DecisionRecord | undefined) ?? null;
};

// Fan-out
export const selectFanOutSelected = (state: RootStateShape): number[] =>
  slice(state).fanOut.selectedEventIds;
export const selectFanOutRunning = (state: RootStateShape): boolean =>
  slice(state).fanOut.isRunning;
export const selectFanOutResults = (state: RootStateShape): Record<number, FanOutResult> =>
  slice(state).fanOut.results;
export interface FanOutProgress {
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
}
export const selectFanOutProgress = (state: RootStateShape): FanOutProgress => {
  const results = Object.values(slice(state).fanOut.results);
  let succeeded = 0;
  let failed = 0;
  let completed = 0;
  for (const r of results) {
    if (r.status === 'success') {
      succeeded += 1;
      completed += 1;
    } else if (r.status === 'failure') {
      failed += 1;
      completed += 1;
    }
  }
  return { total: results.length, completed, succeeded, failed };
};

// UI
export const selectModal = (state: RootStateShape): ContentModerationModal =>
  slice(state).ui.modal;
export const selectKeyboardEnabled = (state: RootStateShape): boolean =>
  slice(state).ui.keyboardEnabled;

// Composed convenience
export const selectAiLean = (state: RootStateShape) => {
  const detail = selectDetailData(state);
  return detail ? aiLeanFromScore(detail.event.aiScore) : 'neutral';
};
